import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, 
  Network, 
  Shield, 
  Users, 
  Globe, 
  Zap, 
  ChevronRight, 
  Search, 
  Bell, 
  Menu, 
  X,
  ArrowRight,
  Check,
  AlertTriangle,
  DollarSign,
  TrendingUp as TrendingUpIcon, // Renamed to avoid conflict
  LineChart as LineChartIcon, // Renamed to avoid conflict
  BarChart as BarChartIcon, // Renamed to avoid conflict
  PieChart as PieChartIcon, // Renamed to avoid conflict
  Activity,
  Gauge,
  ThumbsUp,
  Map,
  UserPlus,
  RefreshCw
} from 'lucide-react';
import { motion } from 'framer-motion';
import { 
  BarChart, 
  Bar, 
  LineChart, 
  Line, 
  PieChart, 
  Pie, 
  Cell, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { checkExecutiveAccess } from '../lib/executiveAuth';

// Sample data for charts
const revenueData = [
  { month: 'Jan', revenue: 45000, expenses: 32000, profit: 13000 },
  { month: 'Feb', revenue: 52000, expenses: 36000, profit: 16000 },
  { month: 'Mar', revenue: 49000, expenses: 33000, profit: 16000 },
  { month: 'Apr', revenue: 58000, expenses: 38000, profit: 20000 },
  { month: 'May', revenue: 63000, expenses: 40000, profit: 23000 },
  { month: 'Jun', revenue: 69000, expenses: 42000, profit: 27000 },
];

const farmerDistributionData = [
  { country: 'Uganda', value: 5000 },
  { country: 'Kenya', value: 3000 },
  { country: 'Tanzania', value: 1500 },
  { country: 'UK', value: 200 },
  { country: 'USA', value: 350 },
];

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088fe'];

const revenueDistributionData = [
  { name: 'Farmers', value: 50 },
  { name: 'AVN Operations', value: 20 },
  { name: 'Cooperative', value: 5 },
  { name: 'Training', value: 5 },
  { name: 'Platform Dev', value: 20 }
];

const exploitationAlerts = [
  { 
    id: 1, 
    title: 'Potential labor issue detected', 
    location: 'Northern Region, Uganda', 
    severity: 'medium', 
    timestamp: '2 hours ago' 
  },
  { 
    id: 2, 
    title: 'Price anomaly in coffee supply chain', 
    location: 'Central Kenya', 
    severity: 'high', 
    timestamp: '5 hours ago' 
  },
  { 
    id: 3, 
    title: 'Supply chain verification pending', 
    location: 'Export terminal, Mombasa', 
    severity: 'low', 
    timestamp: '1 day ago' 
  },
];

const ecosystemMetrics = [
  { name: 'Ethical Certification', value: 85 },
  { name: 'Transparency Score', value: 92 },
  { name: 'Farmer Satisfaction', value: 88 },
  { name: 'Environmental Compliance', value: 79 },
];

const circularityMetrics = [
  { name: 'Waste Recycling', value: 78 },
  { name: 'Water Reuse', value: 65 },
  { name: 'Byproduct Utilization', value: 82 },
  { name: 'Carbon Neutrality', value: 59 },
];

// Sample data for recent signups
const initialSignups = [
  { 
    id: 1,
    name: "Sarah Kimani",
    email: "sarah.k@example.com",
    role: "Farmer",
    location: "Nairobi, Kenya",
    timestamp: "2 minutes ago",
    verified: true,
    stakeholderType: "producer"
  },
  { 
    id: 2,
    name: "Michael Ochieng",
    email: "mochieng@example.com",
    role: "Cooperative Leader",
    location: "Kisumu, Kenya",
    timestamp: "15 minutes ago",
    verified: true,
    stakeholderType: "producer"
  },
  { 
    id: 3,
    name: "Emma Rodriguez",
    email: "emma.r@example.com",
    role: "Retail Buyer",
    location: "London, UK",
    timestamp: "34 minutes ago",
    verified: false,
    stakeholderType: "buyer"
  },
  { 
    id: 4,
    name: "David Mukasa",
    email: "dmukasa@example.com",
    role: "Farmer",
    location: "Jinja, Uganda",
    timestamp: "1 hour ago",
    verified: true,
    stakeholderType: "producer"
  },
  { 
    id: 5,
    name: "Jennifer Wu",
    email: "jwu@example.com",
    role: "Researcher",
    location: "San Francisco, USA",
    timestamp: "2 hours ago",
    verified: true,
    stakeholderType: "partner"
  }
];

const ExecutiveDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [notifications, setNotifications] = useState(3);
  const [signups, setSignups] = useState(initialSignups);
  const [signupFilter, setSignupFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  
  // Check if user has executive access
  useEffect(() => {
    const verifyAccess = async () => {
      const hasAccess = await checkExecutiveAccess();
      
      if (!hasAccess) {
        // Redirect to login if not an executive
        navigate('/ceo-login');
      } else {
        setLoading(false);
      }
    };
    
    verifyAccess();
  }, [navigate]);
  
  // Simulate new user signups coming in
  useEffect(() => {
    const newSignupsInterval = setInterval(() => {
      // Names from diverse backgrounds
      const names = [
        "Aisha Mbeki", "James Olouch", "Maria Sanchez", "Wei Chen", 
        "Priya Patel", "Jamal Ibrahim", "Lucy Nguyen", "Omar Farooq",
        "Isabella Romano", "Raj Sharma", "Sofia Gonzalez", "Kwame Adu"
      ];
      const roles = ["Farmer", "Processor", "Retailer", "Distributor", "NGO Partner", "Researcher"];
      const locations = ["Kampala, Uganda", "Nairobi, Kenya", "Dar es Salaam, Tanzania", "London, UK", "New York, USA"];
      const stakeholderTypes = ["producer", "buyer", "partner", "researcher"];
      
      // Generate a random new signup
      const newSignup = {
        id: signups.length + 1,
        name: names[Math.floor(Math.random() * names.length)],
        email: `user${Math.floor(Math.random() * 10000)}@example.com`,
        role: roles[Math.floor(Math.random() * roles.length)],
        location: locations[Math.floor(Math.random() * locations.length)],
        timestamp: "Just now",
        verified: Math.random() > 0.3, // 70% chance of being verified
        stakeholderType: stakeholderTypes[Math.floor(Math.random() * stakeholderTypes.length)]
      };
      
      // Add to beginning of array and limit to most recent 30
      setSignups(prev => [newSignup, ...prev].slice(0, 30));
    }, 45000); // Add a new signup every 45 seconds
    
    return () => clearInterval(newSignupsInterval);
  }, []);
  
  // Update "Just now" timestamps every minute
  useEffect(() => {
    const timestampInterval = setInterval(() => {
      setSignups(prev => 
        prev.map(signup => {
          if (signup.timestamp === "Just now") {
            return { ...signup, timestamp: "1 minute ago" };
          } else if (signup.timestamp === "1 minute ago") {
            return { ...signup, timestamp: "2 minutes ago" };
          } else if (signup.timestamp.includes("minutes")) {
            const minutes = parseInt(signup.timestamp.split(" ")[0]);
            if (minutes < 59) {
              return { ...signup, timestamp: `${minutes + 1} minutes ago` };
            } else {
              return { ...signup, timestamp: "1 hour ago" };
            }
          }
          return signup;
        })
      );
    }, 60000); // Update every minute
    
    return () => clearInterval(timestampInterval);
  }, []);
  
  const filteredSignups = signupFilter === 'all' 
    ? signups 
    : signups.filter(signup => signup.stakeholderType === signupFilter);
    
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-avn-purple-600"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar */}
      <div className={`bg-[#4B0082] text-white fixed md:relative h-screen z-40 transition-all duration-300 ease-in-out ${
        sidebarOpen ? 'w-64' : 'w-0 md:w-20 overflow-hidden'
      }`}>
        {/* Logo and Company */}
        <div className="p-6 flex items-center gap-3">
          <div className="bg-white/20 p-2 rounded-lg">
            <Building2 className="h-6 w-6 text-white" />
          </div>
          {sidebarOpen && <span className="font-bold text-xl">AVN Earth</span>}
        </div>
        
        {/* Navigation */}
        <nav className="px-4 py-6">
          <ul className="space-y-2">
            <li>
              <a 
                href="#" 
                className="flex items-center gap-3 p-3 rounded-lg bg-white/10 hover:bg-white/20"
              >
                <Gauge className="h-5 w-5" />
                {sidebarOpen && <span>Executive Overview</span>}
              </a>
            </li>
            <li>
              <a 
                href="#" 
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/10"
              >
                <Network className="h-5 w-5" />
                {sidebarOpen && <span>Network Health</span>}
              </a>
            </li>
            <li>
              <a 
                href="#" 
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/10"
              >
                <DollarSign className="h-5 w-5" />
                {sidebarOpen && <span>Financial Performance</span>}
              </a>
            </li>
            <li>
              <a 
                href="#" 
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/10"
              >
                <Shield className="h-5 w-5" />
                {sidebarOpen && <span>Impact & Justice</span>}
              </a>
            </li>
            <li>
              <a 
                href="#" 
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/10"
              >
                <Zap className="h-5 w-5" />
                {sidebarOpen && <span>Technology Metrics</span>}
              </a>
            </li>
            <li>
              <a 
                href="#" 
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/10"
              >
                <Globe className="h-5 w-5" />
                {sidebarOpen && <span>Regional Performance</span>}
              </a>
            </li>
            <li>
              <a 
                href="#" 
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/10"
              >
                <Users className="h-5 w-5" />
                {sidebarOpen && <span>Stakeholder Relations</span>}
              </a>
            </li>
          </ul>
        </nav>
        
        {/* Toggle Button */}
        <button
          className="absolute right-0 top-16 bg-[#4B0082] p-2 rounded-r-lg transform translate-x-full hidden md:block"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          {sidebarOpen ? 
            <ChevronRight className="h-4 w-4 text-white" /> :
            <ChevronRight className="h-4 w-4 text-white transform rotate-180" />
          }
        </button>
      </div>
      
      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        {/* Top Navigation */}
        <header className="bg-white shadow-sm p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
            <h1 className="text-xl font-bold text-gray-800">Executive Dashboard</h1>
          </div>
          <div className="flex items-center gap-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search..."
                className="pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#4B0082] focus:border-transparent"
              />
              <Search className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            </div>
            <div className="relative">
              <Bell className="h-6 w-6 text-gray-500 hover:text-gray-700 cursor-pointer" />
              {notifications > 0 && (
                <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
                  {notifications}
                </div>
              )}
            </div>
            <div className="w-10 h-10 rounded-full bg-[#4B0082] text-white flex items-center justify-center">
              H
            </div>
          </div>
        </header>
        
        {/* Dashboard Content */}
        <main className="p-6 overflow-y-auto" style={{ height: 'calc(100vh - 65px)' }}>
          {/* Welcome Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-2xl font-bold text-gray-800">Welcome, Hamza</h2>
              <div className="text-sm text-gray-500">
                Last updated: {new Date().toLocaleString()}
              </div>
            </div>
            <p className="text-gray-600">
              Here's the current state of AVN's ecosystem and impact metrics
            </p>
          </div>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Total Farmers</p>
                  <h3 className="text-2xl font-bold">10,050</h3>
                  <div className="flex items-center mt-2">
                    <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full flex items-center">
                      <TrendingUpIcon className="h-3 w-3 mr-1" /> +12.5%
                    </span>
                  </div>
                </div>
                <div className="bg-purple-100 p-3 rounded-lg">
                  <Users className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Monthly Revenue</p>
                  <h3 className="text-2xl font-bold">$69,000</h3>
                  <div className="flex items-center mt-2">
                    <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full flex items-center">
                      <TrendingUpIcon className="h-3 w-3 mr-1" /> +9.3%
                    </span>
                  </div>
                </div>
                <div className="bg-green-100 p-3 rounded-lg">
                  <DollarSign className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Ecosystem Health</p>
                  <h3 className="text-2xl font-bold">86%</h3>
                  <div className="flex items-center mt-2">
                    <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full flex items-center">
                      <TrendingUpIcon className="h-3 w-3 mr-1" /> +2.4%
                    </span>
                  </div>
                </div>
                <div className="bg-blue-100 p-3 rounded-lg">
                  <Activity className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.3 }}
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Impact Score</p>
                  <h3 className="text-2xl font-bold">92/100</h3>
                  <div className="flex items-center mt-2">
                    <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full flex items-center">
                      <TrendingUpIcon className="h-3 w-3 mr-1" /> +5.7%
                    </span>
                  </div>
                </div>
                <div className="bg-amber-100 p-3 rounded-lg">
                  <ThumbsUp className="h-6 w-6 text-amber-600" />
                </div>
              </div>
            </motion.div>
          </div>
          
          {/* User Signups Tracking */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.4 }}
            className="bg-white rounded-xl shadow-sm p-6 mb-8"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <UserPlus className="h-6 w-6 text-purple-600" />
                <h3 className="text-lg font-bold text-gray-800">Recent User Signups</h3>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => setSignupFilter('all')}
                    className={`text-xs px-3 py-1.5 rounded-full ${
                      signupFilter === 'all' 
                        ? 'bg-purple-100 text-purple-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    All
                  </button>
                  <button 
                    onClick={() => setSignupFilter('producer')}
                    className={`text-xs px-3 py-1.5 rounded-full ${
                      signupFilter === 'producer' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    Producers
                  </button>
                  <button 
                    onClick={() => setSignupFilter('buyer')}
                    className={`text-xs px-3 py-1.5 rounded-full ${
                      signupFilter === 'buyer' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    Buyers
                  </button>
                  <button 
                    onClick={() => setSignupFilter('partner')}
                    className={`text-xs px-3 py-1.5 rounded-full ${
                      signupFilter === 'partner' 
                        ? 'bg-amber-100 text-amber-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    Partners
                  </button>
                </div>
                <button className="p-2 text-gray-500 hover:text-gray-700 bg-gray-100 rounded-full">
                  <RefreshCw className="h-4 w-4" />
                </button>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Joined
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredSignups.slice(0, 5).map((signup) => (
                    <tr key={signup.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-medium">
                            {signup.name.charAt(0)}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{signup.name}</div>
                            <div className="text-sm text-gray-500">{signup.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{signup.role}</div>
                        <div className="text-xs text-gray-500">
                          {signup.stakeholderType === 'producer' && 'Producer Network'}
                          {signup.stakeholderType === 'buyer' && 'Buyer Network'}
                          {signup.stakeholderType === 'partner' && 'Partner Network'}
                          {signup.stakeholderType === 'researcher' && 'Research Network'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {signup.location}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {signup.verified ? (
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            <Check className="h-3 w-3 mr-1" /> Verified
                          </span>
                        ) : (
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-amber-100 text-amber-800">
                            Pending
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {signup.timestamp}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {filteredSignups.length > 5 && (
              <div className="mt-4 text-center">
                <button className="text-sm text-purple-600 hover:text-purple-800 font-medium flex items-center gap-1 mx-auto">
                  <span>View all {filteredSignups.length} signups</span>
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            )}
            
            {filteredSignups.length === 0 && (
              <div className="text-center py-8">
                <UserPlus className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No signups matching this filter</p>
              </div>
            )}
          </motion.div>
          
          {/* Main Dashboard Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {/* Revenue Chart */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.5 }}
              className="bg-white rounded-xl shadow-sm p-6 lg:col-span-2"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-gray-800">Revenue Performance</h3>
                <div className="flex items-center gap-3">
                  <button className="text-sm px-3 py-1 rounded-full bg-purple-100 text-purple-800">
                    Monthly
                  </button>
                  <button className="text-sm px-3 py-1 rounded-full bg-gray-100 text-gray-800">
                    Quarterly
                  </button>
                  <button className="text-sm px-3 py-1 rounded-full bg-gray-100 text-gray-800">
                    Yearly
                  </button>
                </div>
              </div>
              
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={revenueData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="revenue" 
                      stroke="#8884d8" 
                      strokeWidth={2}
                      name="Revenue"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="expenses" 
                      stroke="#82ca9d" 
                      strokeWidth={2}
                      name="Expenses"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="profit" 
                      stroke="#ffc658" 
                      strokeWidth={2}
                      name="Profit"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </motion.div>
            
            {/* Revenue Distribution */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.6 }}
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <h3 className="text-lg font-bold text-gray-800 mb-6">Revenue Distribution</h3>
              
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={revenueDistributionData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {revenueDistributionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </motion.div>
          </div>
          
          {/* Second Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {/* Exploitation Alerts */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.7 }}
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-gray-800">Exploitation Alerts</h3>
                <a href="#" className="text-sm text-purple-600 hover:text-purple-800">View All</a>
              </div>
              
              <div className="space-y-4">
                {exploitationAlerts.map(alert => (
                  <div key={alert.id} className={`p-4 rounded-lg ${
                    alert.severity === 'high' ? 'bg-red-50' : 
                    alert.severity === 'medium' ? 'bg-amber-50' : 'bg-blue-50'
                  }`}>
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-full ${
                        alert.severity === 'high' ? 'bg-red-100' : 
                        alert.severity === 'medium' ? 'bg-amber-100' : 'bg-blue-100'
                      }`}>
                        <AlertTriangle className={`h-5 w-5 ${
                          alert.severity === 'high' ? 'text-red-600' : 
                          alert.severity === 'medium' ? 'text-amber-600' : 'text-blue-600'
                        }`} />
                      </div>
                      <div>
                        <h4 className={`font-medium ${
                          alert.severity === 'high' ? 'text-red-800' : 
                          alert.severity === 'medium' ? 'text-amber-800' : 'text-blue-800'
                        }`}>
                          {alert.title}
                        </h4>
                        <p className="text-gray-600 text-sm">
                          {alert.location}
                        </p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-gray-500">{alert.timestamp}</span>
                          <button className={`text-xs ${
                            alert.severity === 'high' ? 'text-red-600' : 
                            alert.severity === 'medium' ? 'text-amber-600' : 'text-blue-600'
                          }`}>
                            Investigate →
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
            
            {/* Ecosystem Health */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.8 }}
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <h3 className="text-lg font-bold text-gray-800 mb-6">Ecosystem Health</h3>
              
              <div className="space-y-6">
                {ecosystemMetrics.map(metric => (
                  <div key={metric.name} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">{metric.name}</span>
                      <span className="text-gray-700 font-medium">{metric.value}%</span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${
                          metric.value >= 90 ? 'bg-green-500' : 
                          metric.value >= 75 ? 'bg-blue-500' : 
                          metric.value >= 60 ? 'bg-amber-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${metric.value}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
            
            {/* Circularity Metrics */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.9 }}
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <h3 className="text-lg font-bold text-gray-800 mb-6">Circularity Metrics</h3>
              
              <div className="space-y-6">
                {circularityMetrics.map(metric => (
                  <div key={metric.name} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">{metric.name}</span>
                      <span className="text-gray-700 font-medium">{metric.value}%</span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${
                          metric.value >= 80 ? 'bg-green-500' : 
                          metric.value >= 60 ? 'bg-blue-500' : 
                          metric.value >= 40 ? 'bg-amber-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${metric.value}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
          
          {/* Third Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {/* Farmer Distribution */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 1.0 }}
              className="bg-white rounded-xl shadow-sm p-6 lg:col-span-2"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-gray-800">Farmer Distribution</h3>
                <div className="flex items-center gap-2">
                  <Map className="h-5 w-5 text-gray-500" />
                  <span className="text-sm text-gray-500">Global View</span>
                </div>
              </div>
              
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={farmerDistributionData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
                    <XAxis dataKey="country" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" name="Farmers" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </motion.div>
            
            {/* AI Recommendations */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 1.1 }}
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-gray-800">AI Insights</h3>
                <Zap className="h-5 w-5 text-purple-600" />
              </div>
              
              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-purple-50 border-l-4 border-purple-500">
                  <h4 className="font-medium text-purple-800 mb-2">Strategic Opportunity</h4>
                  <p className="text-sm text-gray-700">
                    Coffee cherry syrup production shows 35% higher margins than expected. Consider scaling this initiative across all hubs.
                  </p>
                  <div className="flex justify-end mt-2">
                    <button className="text-xs text-purple-700 flex items-center gap-1">
                      <span>View Analysis</span>
                      <ArrowRight className="h-3 w-3" />
                    </button>
                  </div>
                </div>
                
                <div className="p-4 rounded-lg bg-amber-50 border-l-4 border-amber-500">
                  <h4 className="font-medium text-amber-800 mb-2">Risk Alert</h4>
                  <p className="text-sm text-gray-700">
                    Weather patterns in Eastern Uganda indicate potential drought conditions in Q3. Prepare mitigation strategies.
                  </p>
                  <div className="flex justify-end mt-2">
                    <button className="text-xs text-amber-700 flex items-center gap-1">
                      <span>View Forecast</span>
                      <ArrowRight className="h-3 w-3" />
                    </button>
                  </div>
                </div>
                
                <div className="p-4 rounded-lg bg-green-50 border-l-4 border-green-500">
                  <h4 className="font-medium text-green-800 mb-2">Growth Indicator</h4>
                  <p className="text-sm text-gray-700">
                    European market analysis shows 42% increase in demand for verified ethical coffee products.
                  </p>
                  <div className="flex justify-end mt-2">
                    <button className="text-xs text-green-700 flex items-center gap-1">
                      <span>Export Details</span>
                      <ArrowRight className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
          
          {/* Approval Queue */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 1.2 }}
            className="bg-white rounded-xl shadow-sm p-6 mb-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-gray-800">Pending Approvals</h3>
              <div className="flex items-center gap-2 bg-purple-100 px-3 py-1 rounded-full">
                <span className="font-medium text-purple-800 text-sm">5</span>
                <span className="text-purple-800 text-sm">Items Requiring Action</span>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requestor</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deadline</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">New Partnership Agreement</div>
                      <div className="text-xs text-gray-500">Legal document requiring signature</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      Sarah Johnson, Partnerships
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">
                        High
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      Tomorrow, 5:00 PM
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button className="px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700">
                          Review
                        </button>
                        <button className="px-3 py-1 bg-gray-100 text-gray-800 rounded hover:bg-gray-200">
                          Delegate
                        </button>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">Q2 Financial Report</div>
                      <div className="text-xs text-gray-500">Final review before board submission</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      Michael Chen, Finance
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-800">
                        Medium
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      Friday, 12:00 PM
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button className="px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700">
                          Review
                        </button>
                        <button className="px-3 py-1 bg-gray-100 text-gray-800 rounded hover:bg-gray-200">
                          Delegate
                        </button>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">New Market Entry Strategy</div>
                      <div className="text-xs text-gray-500">Proposal for Tanzania expansion</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      Emily Nguyen, Strategy
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-800">
                        Medium
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      Monday, 10:00 AM
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button className="px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700">
                          Review
                        </button>
                        <button className="px-3 py-1 bg-gray-100 text-gray-800 rounded hover:bg-gray-200">
                          Delegate
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default ExecutiveDashboard;